import { useState, useCallback, useContext } from 'react';
import { Row, Col, Button, Form, ProgressBar, Alert, Modal, Badge } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import { read, utils } from 'xlsx';
import { v4 as uuidv4 } from 'uuid';
import { db } from 'config/firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { FirebaseAuthContext } from 'context/FirebaseAuthContext';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faDownload, faFileAlt, faTimes, faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import ValidationTable from './ValidationTable';
import {
  parseAmount,
  parseDate,
  validateDateLogic,
  validateSKU,
  checkDuplicate,
  checkProductExists,
  batchWritePromotions
} from '../helpers/promotionHelpers';
import { validateAllConflicts } from '../helpers/conflictDetection';

export default function UploadRibbon({ onComplete }) {
  const { user } = useContext(FirebaseAuthContext);

  const [showModal, setShowModal] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [fileName, setFileName] = useState('');
  const [rawData, setRawData] = useState([]);
  const [validatedRows, setValidatedRows] = useState([]);
  const [skipExpired, setSkipExpired] = useState(true);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);
  const [batchId, setBatchId] = useState(null);

  const onDrop = useCallback(acceptedFiles => {
    console.log('[UploadRibbon] Files dropped:', acceptedFiles.length);
    acceptedFiles.forEach((file) => {
      console.log('[UploadRibbon] Processing file:', file.name);
      setFileName(file.name);
      const reader = new FileReader();

      reader.onerror = () => {
        console.error('[UploadRibbon] File reading failed');
        toast.error('File reading failed');
      };
      reader.onload = () => {
        console.log('[UploadRibbon] File loaded, parsing...');
        try {
          const binaryStr = reader.result;
          const workbook = read(binaryStr, { type: 'binary' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const data = utils.sheet_to_json(worksheet);

          if (data.length === 0) {
            toast.error('CSV file is empty');
            return;
          }

          const requiredColumns = ['SKU', 'Promotion Name', 'Discount Amount', 'Start Date', 'End Date'];
          const firstRow = data[0];
          const missingColumns = requiredColumns.filter(col => !(col in firstRow));

          if (missingColumns.length > 0) {
            toast.error(`Missing required columns: ${missingColumns.join(', ')}`);
            return;
          }

          console.log('[UploadRibbon] Parsed rows:', data.length);
          setRawData(data);
          setShowModal(true);
          console.log('[UploadRibbon] Modal should now be visible');
          toast.success(`Loaded ${data.length} rows from ${file.name}`);
        } catch (error) {
          console.error('Parse error:', error);
          toast.error('Failed to parse CSV file');
        }
      };

      reader.readAsArrayBuffer(file);
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: '.csv, .xls, .xlsx',
    multiple: false,
    noClick: false
  });

  async function handleValidate() {
    if (rawData.length === 0) {
      toast.error('No data to validate');
      return;
    }

    setValidating(true);
    setWizardStep(2);
    const validated = [];

    try {
      for (let i = 0; i < rawData.length; i++) {
        const row = rawData[i];
        const rowNumber = i + 2;
        const errors = [];
        const warnings = [];

        const skuValidation = validateSKU(row['SKU']);
        if (skuValidation.error) errors.push(skuValidation.error);

        const amountValidation = parseAmount(row['Discount Amount']);
        if (!amountValidation.valid) errors.push(amountValidation.error);

        const startDateValidation = parseDate(row['Start Date']);
        const endDateValidation = parseDate(row['End Date']);

        if (!startDateValidation.valid) errors.push(`Start Date: ${startDateValidation.error}`);
        if (!endDateValidation.valid) errors.push(`End Date: ${endDateValidation.error}`);

        let dateLogicValid = false;
        if (startDateValidation.valid && endDateValidation.valid) {
          const dateLogic = validateDateLogic(startDateValidation.value, endDateValidation.value, skipExpired);
          if (!dateLogic.valid) errors.push(dateLogic.error);
          else if (dateLogic.warning) warnings.push(dateLogic.warning);
          else dateLogicValid = true;
        }

        let productExists = null;
        if (!skuValidation.error && !skuValidation.isVariant) {
          productExists = await checkProductExists(String(row['SKU']).trim());
          if (!productExists.exists) errors.push(`SKU ${row['SKU']} not found in database`);
        }

        let isDuplicate = false;
        if (productExists?.exists && dateLogicValid && amountValidation.valid) {
          const newDiscount = {
            name: row['Promotion Name'],
            value: amountValidation.value,
            startDate: startDateValidation.value,
            endDate: endDateValidation.value
          };
          isDuplicate = checkDuplicate(newDiscount, productExists.discounts);
          if (isDuplicate) warnings.push('Duplicate promotion already exists');
        }

        validated.push({
          rowNumber,
          sku: row['SKU'],
          name: row['Promotion Name'],
          value: amountValidation.valid ? amountValidation.value : row['Discount Amount'],
          startDate: startDateValidation.valid ? startDateValidation.value : null,
          endDate: endDateValidation.valid ? endDateValidation.value : null,
          notes: row['Notes'] || '',
          errors,
          warnings,
          existingDiscounts: productExists?.discounts || [],
          productDocRef: productExists?.docRef || null,
          isDuplicate
        });
      }

      const conflictResults = validateAllConflicts(validated);
      validated.forEach((row, index) => {
        if (conflictResults.rowConflicts.has(index)) {
          const conflicts = conflictResults.rowConflicts.get(index);
          row.conflicts = conflicts;
          row.errors = [...(row.errors || []), ...conflicts];
        }
      });

      setValidatedRows(validated);
      setValidating(false);
      setWizardStep(3);
    } catch (error) {
      console.error('Validation error:', error);
      toast.error(`Validation failed: ${error.message}`);
      setValidating(false);
      setWizardStep(1);
    }
  }

  async function handleImport() {
    const validRows = validatedRows.filter(r => (!r.errors || r.errors.length === 0) && !r.isDuplicate);

    if (validRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }

    setImporting(true);
    setWizardStep(4);
    const newBatchId = uuidv4();
    setBatchId(newBatchId);

    try {
      await addDoc(collection(db, 'promotionImportHistory'), {
        batchId: newBatchId,
        uploadedBy: user.email,
        uploadedAt: Timestamp.now(),
        fileName,
        recordsAdded: 0,
        recordsFailed: 0,
        status: 'in_progress'
      });

      const results = await batchWritePromotions(
        validRows,
        user,
        newBatchId,
        (current, total) => setImportProgress(Math.round((current / total) * 100))
      );

      setImportResults(results);
      toast.success(`Successfully imported ${results.success} promotions`);

      if (results.failed.length > 0) {
        toast.warning(`${results.failed.length} rows failed to import`);
      }

      if (onComplete) onComplete();
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Import failed');
    } finally {
      setImporting(false);
    }
  }

  function handleReset() {
    setShowModal(false);
    setWizardStep(1);
    setFileName('');
    setRawData([]);
    setValidatedRows([]);
    setImportProgress(0);
    setImportResults(null);
    setBatchId(null);
  }

  function downloadTemplate(withExample = false) {
    const headers = ['SKU', 'Promotion Name', 'Discount Amount', 'Start Date', 'End Date', 'Notes'];
    const rows = [headers];

    if (withExample) {
      rows.push(['123456', 'Summer Sale', '5.00', '2024-06-01', '2024-08-31', 'Optional notes']);
      rows.push(['789012', 'Holiday Special', '10.00', '2024-12-01', '2024-12-31', '']);
    }

    const csvContent = rows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = withExample ? 'promotions_example.csv' : 'promotions_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const validCount = validatedRows.filter(r => (!r.errors || r.errors.length === 0) && !r.isDuplicate).length;
  const errorCount = validatedRows.filter(r => r.errors && r.errors.length > 0).length;

  return (
    <>
      {/* Compact Ribbon */}
      <div className="bg-light border rounded p-2 mb-3">
        <Row className="align-items-center g-2">
          <Col xs="auto">
            <div
              {...getRootProps()}
              className={`d-inline-flex align-items-center px-3 py-2 rounded cursor-pointer ${isDragActive ? 'bg-primary text-white' : 'bg-white border'}`}
              style={{ cursor: 'pointer' }}
            >
              <input {...getInputProps()} />
              <FontAwesomeIcon icon={faUpload} className="me-2" />
              <span className="fs--1">
                {isDragActive ? 'Drop file here...' : 'Upload CSV'}
              </span>
            </div>
          </Col>
          <Col xs="auto">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => downloadTemplate(false)}
              className="me-1"
            >
              <FontAwesomeIcon icon={faFileAlt} className="me-1" />
              Template
            </Button>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => downloadTemplate(true)}
            >
              <FontAwesomeIcon icon={faDownload} className="me-1" />
              Example
            </Button>
          </Col>
          <Col className="text-end">
            <span className="text-muted fs--2">
              Format: SKU, Promotion Name, Discount Amount, Start Date (YYYY-MM-DD), End Date
            </span>
          </Col>
        </Row>
      </div>

      {/* Upload Modal */}
      <Modal show={showModal} onHide={handleReset} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            {wizardStep === 1 && `Import: ${fileName}`}
            {wizardStep === 2 && 'Validating...'}
            {wizardStep === 3 && 'Review & Import'}
            {wizardStep === 4 && (importing ? 'Importing...' : 'Import Complete')}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Step 1: Confirm file */}
          {wizardStep === 1 && (
            <div className="text-center py-4">
              <Alert variant="info">
                <strong>{fileName}</strong> contains <strong>{rawData.length}</strong> rows
              </Alert>
              <Form.Check
                type="checkbox"
                label="Skip expired promotions"
                checked={skipExpired}
                onChange={(e) => setSkipExpired(e.target.checked)}
                className="mb-3"
              />
              <Button variant="primary" onClick={handleValidate}>
                Validate Data
              </Button>
            </div>
          )}

          {/* Step 2: Validating */}
          {wizardStep === 2 && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary mb-3" />
              <p>Validating {rawData.length} rows...</p>
            </div>
          )}

          {/* Step 3: Review */}
          {wizardStep === 3 && (
            <>
              <Alert variant={errorCount > 0 ? 'warning' : 'success'} className="mb-3">
                <Row className="align-items-center">
                  <Col>
                    <Badge bg="success" className="me-2">{validCount} Valid</Badge>
                    {errorCount > 0 && <Badge bg="danger">{errorCount} Errors</Badge>}
                  </Col>
                  <Col xs="auto">
                    <Button
                      variant="primary"
                      onClick={handleImport}
                      disabled={validCount === 0}
                    >
                      <FontAwesomeIcon icon={faCheck} className="me-1" />
                      Import {validCount} Promotions
                    </Button>
                  </Col>
                </Row>
              </Alert>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <ValidationTable rows={validatedRows} />
              </div>
            </>
          )}

          {/* Step 4: Import */}
          {wizardStep === 4 && (
            <div className="text-center py-4">
              {importing ? (
                <>
                  <h5 className="mb-3">Importing Promotions...</h5>
                  <ProgressBar now={importProgress} label={`${importProgress}%`} style={{ height: '25px' }} />
                  <p className="text-muted mt-2">Please wait...</p>
                </>
              ) : importResults && (
                <>
                  <FontAwesomeIcon icon={faCheck} className="text-success mb-3" size="3x" />
                  <h4>Import Complete!</h4>
                  <p><strong>{importResults.success}</strong> promotions imported successfully</p>
                  {importResults.failed.length > 0 && (
                    <p className="text-danger">{importResults.failed.length} rows failed</p>
                  )}
                  <Button variant="primary" onClick={handleReset}>Done</Button>
                </>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
